import { useEffect, useMemo, useState } from "react";
import DateInput from "@/components/DateInput";
import api, { fmtMoney, fmtDate } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { ArrowUp, ArrowDown, Download, FunnelSimple, X, Warning, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import CuentaCombobox from "@/components/CuentaCombobox";
import { ConciliadoBadge } from "@/components/ConciliarMovimientoDialog";
import NumberInput from "@/components/ui/NumberInput";

const EMPTY_FILTERS = {
  caja_id: "__all__",
  tipo: "__all__",
  clasificado: "__all__",
  cuenta_codigo: "",
  concepto: "",
  fecha_desde: "",
  fecha_hasta: "",
  monto_min: "",
  monto_max: "",
};

export default function MovimientosAllTab({ cajas, cuentas }) {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [movs, setMovs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reclasifMov, setReclasifMov] = useState(null);
  const [reclasifValue, setReclasifValue] = useState("");

  const buildParams = () => {
    const p = {};
    if (filters.caja_id !== "__all__") p.caja_id = filters.caja_id;
    if (filters.tipo !== "__all__") p.tipo = filters.tipo;
    if (filters.clasificado !== "__all__") p.clasificado = filters.clasificado;
    if (filters.cuenta_codigo) p.cuenta_codigo = filters.cuenta_codigo;
    if (filters.concepto.trim()) p.concepto = filters.concepto.trim();
    if (filters.fecha_desde) p.fecha_desde = filters.fecha_desde;
    if (filters.fecha_hasta) p.fecha_hasta = filters.fecha_hasta;
    if (filters.monto_min !== "") p.monto_min = parseFloat(filters.monto_min);
    if (filters.monto_max !== "") p.monto_max = parseFloat(filters.monto_max);
    return p;
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/movimientos-caja", { params: buildParams() });
      setMovs(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [filters]);

  const exportExcel = async () => {
    const BACKEND = process.env.REACT_APP_BACKEND_URL;
    const params = new URLSearchParams();
    Object.entries(buildParams()).forEach(([k, v]) => params.append(k, v));
    const token = localStorage.getItem("erp_token");
    const resp = await fetch(`${BACKEND}/api/movimientos-caja/export?${params}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!resp.ok) {
      toast.error("Error al exportar");
      return;
    }
    const blob = await resp.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `movimientos_${new Date().toISOString().slice(0, 10)}.xlsx`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const totals = useMemo(() => {
    let ing = 0, egr = 0;
    movs.forEach((m) => {
      if (m.tipo === "ingreso") ing += m.monto;
      else if (m.tipo === "egreso") egr += m.monto;
    });
    return { ing, egr, neto: ing - egr };
  }, [movs]);

  const reset = () => setFilters(EMPTY_FILTERS);
  const hasFilters = JSON.stringify(filters) !== JSON.stringify(EMPTY_FILTERS);

  const openReclasif = (m) => {
    setReclasifMov(m);
    setReclasifValue(m.cuenta_codigo || "");
  };

  const saveReclasif = async () => {
    if (!reclasifMov || !reclasifValue) {
      toast.error("Seleccioná una cuenta contable");
      return;
    }
    try {
      await api.patch(`/movimientos-caja/${reclasifMov.id}/clasificar`, {
        cuenta_codigo: reclasifValue,
      });
      toast.success("Cuenta actualizada");
      setReclasifMov(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };

  return (
    <div className="border border-[#E5E5E5] bg-white rounded-sm overflow-hidden">
      <div className="px-5 py-3 border-b bg-neutral-50 flex items-center justify-between">
        <div>
          <div className="font-heading font-semibold text-[15px] flex items-center gap-2">
            <FunnelSimple size={16} weight="bold" className="text-[#6B1F24]" />
            Todos los movimientos
          </div>
          <div className="text-[11px] text-[#666] mt-0.5">
            {loading ? "Cargando…" : `${movs.length} movimientos · Ingresos ${fmtMoney(totals.ing)} · Egresos ${fmtMoney(totals.egr)} · Neto ${fmtMoney(totals.neto)}`}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {hasFilters && (
            <button
              data-testid="clear-filters-btn"
              onClick={reset}
              className="text-[11px] text-[#666] hover:text-[#6B1F24] flex items-center gap-1 px-2 py-1 rounded-sm hover:bg-neutral-100"
            >
              <X size={12} /> Limpiar filtros
            </button>
          )}
          <Button
            data-testid="export-excel-btn"
            onClick={exportExcel}
            className="rounded-sm bg-[#0E793C] hover:bg-[#0a5c2d] h-8 text-[12px]"
          >
            <Download size={14} className="mr-1" weight="bold" />
            Exportar Excel
          </Button>
        </div>
      </div>

      <div className="px-5 py-3 border-b bg-white grid grid-cols-12 gap-2 text-[11px]">
        <div className="col-span-2">
          <label className="uppercase font-mono text-[#666] tracking-widest">Caja</label>
          <Select value={filters.caja_id} onValueChange={(v) => setFilters({ ...filters, caja_id: v })}>
            <SelectTrigger className="rounded-sm h-8 mt-1 text-[12px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Todas</SelectItem>
              {cajas.map((c) => <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="col-span-1">
          <label className="uppercase font-mono text-[#666] tracking-widest">Tipo</label>
          <Select value={filters.tipo} onValueChange={(v) => setFilters({ ...filters, tipo: v })}>
            <SelectTrigger className="rounded-sm h-8 mt-1 text-[12px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Todos</SelectItem>
              <SelectItem value="ingreso">Ingreso</SelectItem>
              <SelectItem value="egreso">Egreso</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="col-span-2">
          <label className="uppercase font-mono text-[#666] tracking-widest">Concepto</label>
          <Input
            value={filters.concepto}
            onChange={(e) => setFilters({ ...filters, concepto: e.target.value })}
            className="rounded-sm h-8 mt-1 text-[12px]"
            placeholder="Buscar…"
          />
        </div>
        <div className="col-span-3">
          <label className="uppercase font-mono text-[#666] tracking-widest">Cuenta contable</label>
          <div className="mt-1">
            <CuentaCombobox
              value={filters.cuenta_codigo}
              onChange={(v) => setFilters({ ...filters, cuenta_codigo: v })}
              cuentas={cuentas}
              compact
              placeholder="Todas las cuentas"
            />
          </div>
        </div>
        <div className="col-span-1">
          <label className="uppercase font-mono text-[#666] tracking-widest">Estado</label>
          <Select value={filters.clasificado} onValueChange={(v) => setFilters({ ...filters, clasificado: v })}>
            <SelectTrigger className="rounded-sm h-8 mt-1 text-[12px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Todos</SelectItem>
              <SelectItem value="true">Clasif.</SelectItem>
              <SelectItem value="false">Sin clasif.</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="col-span-1">
          <label className="uppercase font-mono text-[#666] tracking-widest">Desde</label>
          <DateInput
            value={filters.fecha_desde}
            onChange={(e) => setFilters({ ...filters, fecha_desde: e.target.value })}
            className="rounded-sm h-8 mt-1 text-[11px] font-mono"
          />
        </div>
        <div className="col-span-1">
          <label className="uppercase font-mono text-[#666] tracking-widest">Hasta</label>
          <DateInput
            value={filters.fecha_hasta}
            onChange={(e) => setFilters({ ...filters, fecha_hasta: e.target.value })}
            className="rounded-sm h-8 mt-1 text-[11px] font-mono"
          />
        </div>
        <div className="col-span-1">
          <label className="uppercase font-mono text-[#666] tracking-widest">Min $</label>
          <NumberInput
            value={filters.monto_min}
            onChange={(e) => setFilters({ ...filters, monto_min: e.target.value })}
            className="rounded-sm h-8 mt-1 text-[11px] font-mono text-right"
          />
        </div>
      </div>

      <div className="overflow-x-auto max-h-[calc(100vh-360px)] overflow-y-auto">
        <table className="w-full text-[13px] grid-table">
          <thead className="text-[11px] uppercase font-mono text-[#666] bg-neutral-50 sticky top-0 z-10">
            <tr>
              <th className="px-4 py-2 text-left w-24">Fecha</th>
              <th className="px-4 py-2 text-left w-40">Caja</th>
              <th className="px-4 py-2 text-left w-20">Tipo</th>
              <th className="px-4 py-2 text-left">Concepto</th>
              <th className="px-4 py-2 text-left w-56">Cuenta contable</th>
              <th className="px-4 py-2 text-right w-32">Monto</th>
            </tr>
          </thead>
          <tbody>
            {movs.map((m) => (
              <tr key={m.id} data-testid={`allmov-${m.id}`}>
                <td className="px-4 py-2 font-mono text-[12px]">{fmtDate(m.fecha)}</td>
                <td className="px-4 py-2 text-[#666]">{m.caja_nombre}</td>
                <td className="px-4 py-2">
                  <span className={`inline-flex items-center gap-1 text-[11px] ${m.tipo === "ingreso" ? "num-pos" : "num-neg"}`}>
                    {m.tipo === "ingreso" ? <ArrowUp size={12} /> : <ArrowDown size={12} />} {m.tipo}
                  </span>
                </td>
                <td className="px-4 py-2 max-w-md">
                  <span className="truncate inline-block max-w-full align-middle" title={m.concepto}>{m.concepto}</span>
                  <ConciliadoBadge mov={m} />
                </td>
                <td className="px-4 py-2 font-mono text-[11px] text-[#666]">
                  {m.clasificado === false ? (
                    <button
                      onClick={() => openReclasif(m)}
                      data-testid={`reclasif-${m.id}`}
                      className="inline-flex items-center gap-1 text-amber-700 hover:text-amber-900 group/rc"
                      title="Asignar cuenta contable"
                    >
                      <Warning size={12} weight="fill" /> Sin clasificar
                      <PencilSimple size={11} className="opacity-0 group-hover/rc:opacity-100 transition-opacity" />
                    </button>
                  ) : m.cuenta_codigo ? (
                    <button
                      onClick={() => openReclasif(m)}
                      data-testid={`reclasif-${m.id}`}
                      className="inline-flex items-center gap-1 hover:text-[#6B1F24] group/rc text-left"
                      title="Cambiar cuenta contable"
                    >
                      <span><span className="text-[#6B1F24]">{m.cuenta_codigo}</span> · {m.cuenta_nombre}</span>
                      <PencilSimple size={11} className="opacity-0 group-hover/rc:opacity-100 transition-opacity flex-shrink-0" />
                    </button>
                  ) : "-"}
                </td>
                <td className={`px-4 py-2 text-right font-mono ${m.tipo === "ingreso" ? "num-pos" : "num-neg"}`}>
                  {m.tipo === "ingreso" ? "+" : "-"} {fmtMoney(m.monto, m.moneda)}
                </td>
              </tr>
            ))}
            {movs.length === 0 && !loading && (
              <tr><td colSpan={6} className="text-center py-12 text-[#999]">Sin movimientos con estos filtros.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Sheet open={!!reclasifMov} onOpenChange={(v) => !v && setReclasifMov(null)}>
        <SheetContent className="w-full sm:w-[440px] sm:max-w-[440px]">
          <SheetHeader>
            <SheetTitle className="font-heading">
              {reclasifMov?.clasificado === false ? "Asignar cuenta contable" : "Cambiar cuenta contable"}
            </SheetTitle>
          </SheetHeader>
          {reclasifMov && (
            <div className="mt-6 space-y-4">
              <div className="border border-[#E5E5E5] rounded-sm p-3 bg-neutral-50 text-[12px] space-y-1">
                <div><span className="text-[#666]">Fecha:</span> <span className="font-mono">{fmtDate(reclasifMov.fecha)}</span></div>
                <div><span className="text-[#666]">Caja:</span> {reclasifMov.caja_nombre}</div>
                <div><span className="text-[#666]">Concepto:</span> {reclasifMov.concepto}</div>
                <div>
                  <span className="text-[#666]">Monto:</span>{" "}
                  <span className={`font-mono font-semibold ${reclasifMov.tipo === "ingreso" ? "num-pos" : "num-neg"}`}>
                    {reclasifMov.tipo === "ingreso" ? "+" : "-"} {fmtMoney(reclasifMov.monto, reclasifMov.moneda)}
                  </span>
                </div>
                {reclasifMov.cuenta_codigo && (
                  <div><span className="text-[#666]">Cuenta actual:</span>{" "}
                    <span className="font-mono text-[#6B1F24]">{reclasifMov.cuenta_codigo}</span>
                    {" · "}{reclasifMov.cuenta_nombre}
                  </div>
                )}
              </div>
              <div>
                <Label className="text-[12px]">
                  {reclasifMov.clasificado === false ? "Cuenta contable" : "Nueva cuenta contable"}
                </Label>
                <div className="mt-1">
                  <CuentaCombobox
                    testid="reclasif-combobox"
                    value={reclasifValue}
                    onChange={setReclasifValue}
                    cuentas={cuentas}
                  />
                </div>
              </div>
              <Button
                data-testid="save-reclasif-btn"
                onClick={saveReclasif}
                className="w-full rounded-sm bg-[#6B1F24] hover:bg-[#571519]"
              >
                Guardar
              </Button>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
